// Attachment staging/upload/bind lifecycle for the support forms (Story #4613,
// Stream 8a). Each added file runs stage → presigned-POST upload → bind, with
// per-file progress and a Retry that ALWAYS re-stages (never re-submits a
// possibly-expired presigned POST — a stale 5-min POST is exactly the failure
// mode the re-stage guards against).

import { useCallback, useMemo, useRef, useState } from 'react'
import {
  bindAttachment,
  stageAttachment,
  uploadToPresignedPost,
} from '../services/support'
import { MAX_FILES, isVideoType, maxBytesFor } from '../constants/support'

export type AttachmentStatus =
  | 'queued'
  | 'staging'
  | 'uploading'
  | 'binding'
  | 'done'
  | 'error'

export interface LocalAttachment {
  localId: string
  filename: string
  sizeBytes: number
  contentType: string
  isVideo: boolean
  status: AttachmentStatus
  progress: number
  attachmentId?: string
  error?: string
  /** Restored from a draft — chip is filename/icon only, never re-uploaded. */
  restored?: boolean
}

function uid(): string {
  return `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/** Validate a File against the type/size allowlist. Returns an error string or null. */
export function validateFile(file: File): string | null {
  const max = maxBytesFor(file.type)
  if (max === null) return `${file.name}: unsupported file type`
  if (file.size > max) {
    const mb = Math.round(max / (1024 * 1024))
    return `${file.name}: exceeds the ${mb} MB limit`
  }
  return null
}

interface UseSupportAttachmentsOptions {
  /** Resolves to the ticket id to stage against; may lazily create the ticket. */
  ensureTicketId: () => Promise<string>
  /** Requester lifetime cap reached (server `attachments_locked`). */
  lifetimeLocked?: boolean
}

export interface UseSupportAttachments {
  attachments: LocalAttachment[]
  addFiles: (files: FileList | File[]) => void
  remove: (localId: string) => void
  retry: (localId: string) => void
  restore: (items: { filename: string; sizeBytes: number; contentType: string; isVideo: boolean }[]) => void
  reset: () => void
  fileCount: number
  videoCount: number
  capReached: boolean
  hasVideo: boolean
  /** Every non-restored attachment reached a terminal state (done/error). */
  allSettled: boolean
  /** True while any attachment is still staging/uploading/binding. */
  uploading: boolean
  boundAttachmentIds: string[]
}

export function useSupportAttachments({
  ensureTicketId,
  lifetimeLocked = false,
}: UseSupportAttachmentsOptions): UseSupportAttachments {
  const [attachments, setAttachments] = useState<LocalAttachment[]>([])
  const filesRef = useRef<Map<string, File>>(new Map())

  const patch = useCallback((localId: string, next: Partial<LocalAttachment>) => {
    setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, ...next } : a)))
  }, [])

  const process = useCallback(
    async (localId: string) => {
      const file = filesRef.current.get(localId)
      if (!file) return
      try {
        patch(localId, { status: 'staging', error: undefined, progress: 0 })
        const ticketId = await ensureTicketId()
        // Re-stage every attempt: the presigned POST is single-use and short-lived.
        const staged = await stageAttachment(ticketId, {
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
        })
        patch(localId, { status: 'uploading', attachmentId: staged.attachment_id })
        await uploadToPresignedPost(staged.upload, file, (fraction) =>
          patch(localId, { progress: fraction }),
        )
        patch(localId, { status: 'binding', progress: 1 })
        await bindAttachment(ticketId, staged.attachment_id, staged.upload_id)
        patch(localId, { status: 'done' })
      } catch (e) {
        patch(localId, {
          status: 'error',
          error: e instanceof Error ? e.message : 'Upload failed — try again',
        })
      }
    },
    [ensureTicketId, patch],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming)
      setAttachments((prev) => {
        const active = prev.filter((a) => a.status !== 'error')
        let count = active.length
        let videoTaken = active.some((a) => a.isVideo)
        const additions: LocalAttachment[] = []
        for (const file of list) {
          if (lifetimeLocked) break
          if (count >= MAX_FILES) break
          const isVid = isVideoType(file.type)
          if (isVid && videoTaken) continue // one video per ticket
          const localId = uid()
          const error = validateFile(file)
          filesRef.current.set(localId, file)
          additions.push({
            localId,
            filename: file.name,
            sizeBytes: file.size,
            contentType: file.type,
            isVideo: isVid,
            status: error ? 'error' : 'queued',
            progress: 0,
            error: error ?? undefined,
          })
          if (!error) {
            count += 1
            if (isVid) videoTaken = true
          }
        }
        // Kick off uploads for the valid additions after state commits.
        additions
          .filter((a) => a.status === 'queued')
          .forEach((a) => queueMicrotask(() => process(a.localId)))
        return [...prev, ...additions]
      })
    },
    [lifetimeLocked, process],
  )

  const remove = useCallback((localId: string) => {
    filesRef.current.delete(localId)
    setAttachments((prev) => prev.filter((a) => a.localId !== localId))
  }, [])

  const retry = useCallback(
    (localId: string) => {
      // Retry re-runs the whole pipeline from stage (never re-POSTs a stale URL).
      if (filesRef.current.has(localId)) void process(localId)
    },
    [process],
  )

  const restore = useCallback(
    (items: { filename: string; sizeBytes: number; contentType: string; isVideo: boolean }[]) => {
      setAttachments((prev) => [
        ...prev,
        ...items.map((i) => ({
          localId: uid(),
          filename: i.filename,
          sizeBytes: i.sizeBytes,
          contentType: i.contentType,
          isVideo: i.isVideo,
          status: 'error' as AttachmentStatus,
          progress: 0,
          restored: true,
          error: 'Re-add this file to include it',
        })),
      ])
    },
    [],
  )

  const reset = useCallback(() => {
    filesRef.current.clear()
    setAttachments([])
  }, [])

  const active = useMemo(() => attachments.filter((a) => a.status !== 'error'), [attachments])
  const fileCount = active.length
  const videoCount = active.filter((a) => a.isVideo).length
  const boundAttachmentIds = useMemo(
    () => attachments.filter((a) => a.status === 'done' && a.attachmentId).map((a) => a.attachmentId as string),
    [attachments],
  )

  return {
    attachments,
    addFiles,
    remove,
    retry,
    restore,
    reset,
    fileCount,
    videoCount,
    capReached: fileCount >= MAX_FILES,
    hasVideo: videoCount > 0,
    allSettled: attachments
      .filter((a) => !a.restored)
      .every((a) => a.status === 'done' || a.status === 'error'),
    uploading: attachments.some((a) => ['staging', 'uploading', 'binding'].includes(a.status)),
    boundAttachmentIds,
  }
}
